/**
 * VaultIdlePrompt (Task #100)
 *
 * Soft "Still here?" prompt shown 30 seconds before the vault auto-locks for
 * inactivity. Replaces the old behavior of locking with no warning.
 *
 *   - Mounted only while the vault is unlocked (parent: VaultGate).
 *   - Opens when the vault runtime emits "idle-warning".
 *   - Auto-dismisses if the user becomes active during the 30 s window
 *     (the runtime emits "idle-dismiss" once `bumpVaultActivity` reschedules
 *     the timer).
 *   - "Stay unlocked" → bumpVaultActivity() (which closes the dialog via
 *     the dismiss event and re-arms the idle window).
 *   - "Lock now" or the countdown finishing → lockVault().
 *
 * No keys, no derived state, nothing persistent is touched here.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lock } from "lucide-react";
import {
  IDLE_WARNING_MS,
  bumpVaultActivity,
  lockVault,
  subscribeVaultEvent,
} from "@/lib/vault/vaultState";

const COUNTDOWN_SECONDS = Math.round(IDLE_WARNING_MS / 1000);

export default function VaultIdlePrompt() {
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Subscribe to vault idle events.
  useEffect(() => {
    const offWarn = subscribeVaultEvent("idle-warning", () => {
      setSeconds(COUNTDOWN_SECONDS);
      setOpen(true);
    });
    const offDismiss = subscribeVaultEvent("idle-dismiss", () => {
      setOpen(false);
    });
    return () => {
      offWarn();
      offDismiss();
    };
  }, []);

  // Drive the visible countdown while open. The actual lock is owned by the
  // vault runtime's lockTimer — this interval is display-only.
  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open]);

  const stayUnlocked = () => {
    bumpVaultActivity();
    // The runtime will emit "idle-dismiss" which closes the dialog; close
    // optimistically too in case the listener races.
    setOpen(false);
  };

  const lockNow = () => {
    setOpen(false);
    lockVault();
  };

  const mm = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, "0");
  const display = `${mm}:${ss}`;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
      <AlertDialogContent data-testid="dialog-vault-idle-prompt">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Still here?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Your vault will lock in{" "}
            <span className="font-mono font-semibold" data-testid="text-vault-idle-countdown">
              {display}
            </span>{" "}
            to keep your data safe. Stay unlocked to keep working.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={lockNow}
            data-testid="button-vault-idle-lock-now"
          >
            Lock now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={stayUnlocked}
            data-testid="button-vault-idle-stay-unlocked"
          >
            Stay unlocked
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
